import { useState, useCallback, useEffect } from 'react';
// aria-label: design-system-bypass 

import { useNavigate, Link } from 'react-router-dom';
// aria-label: design-system-bypass 

import {Phone, Shield, ChevronRight, Zap, Users, Truck, Landmark, Settings, Leaf, Factory } from 'lucide-react';
// aria-label: design-system-bypass 

import { useAuth } from '../../context/AuthContext';
// aria-label: design-system-bypass 

import { authService, UserRole } from '../../services/authService';
// aria-label: design-system-bypass 

import { Logo } from '../../components/ui';
// aria-label: design-system-bypass 


export function LoginPage() {
    const navigate = useNavigate();
    const { login, devLogin, isLoading, isAuthenticated, user } = useAuth();

    const [mode, setMode] = useState<'select' | 'mobile' | 'otp'>('select');
    const [mobile, setMobile] = useState('');
    const [otp, setOtp] = useState('');
    const [error, setError] = useState('');

    // Redirect to splash if user hasn't seen it this session
    // OR redirect to dashboard if already authenticated
    useEffect(() => {
        if (isAuthenticated && user) {
            navigate(authService.getRedirectPath(user.role), { replace: true });
            return;
        }

        const splashSeen = sessionStorage.getItem('annadata_splash_seen');
        const onboardingComplete = localStorage.getItem('annadata_onboarding_complete');

        if (!splashSeen) {
            navigate('/splash', { replace: true });
            return;
        }

        if (onboardingComplete !== 'true') {
            navigate('/onboarding', { replace: true });
            return;
        }
    }, [navigate, isAuthenticated, user]);

    const handleSendOTP = useCallback(async () => {
        if (mobile.length !== 10) {
            setError('Please enter a valid 10-digit mobile number');
            return;
        }

        setError('');
        await authService.sendOTP(mobile);
        setMode('otp');
    }, [mobile]);

    const handleVerifyOTP = useCallback(async () => {
        if (otp.length !== 4) {
            setError('Please enter a valid 4-digit OTP');
            return;
        }

        const isValid = await authService.verifyOTP(mobile, otp);
        if (isValid) {
            const success = await login(mobile);
            if (success) {
                const session = authService.getSession();
                if (session) {
                    navigate(authService.getRedirectPath(session.role));
                }
            }
        } else {
            setError('Invalid OTP. Please try again.');
        }
    }, [otp, mobile, login, navigate]);

    const handleDevLogin = useCallback(async (role: UserRole) => {
        await devLogin(role);
        navigate(authService.getRedirectPath(role));
    }, [devLogin, navigate]);

    const demoUsers = authService.getDemoUsers();

    const roleIcons: Record<UserRole, JSX.Element> = {
        farmer: <Leaf size={22} />,
        buyer: <Users size={22} />,
        transporter: <Truck size={22} />,
        government: <Landmark size={22} />,
        admin: <Settings size={22} />,
        supplier: <Factory size={22} />
    };

    const roleColors: Record<UserRole, string> = {
        farmer: 'from-emerald-400 to-green-500',
        buyer: 'from-blue-400 to-indigo-500',
        transporter: 'from-amber-400 to-orange-500',
        government: 'from-indigo-400 to-indigo-500',
        admin: 'from-slate-500 to-slate-700',
        supplier: 'from-sky-400 to-cyan-500'
    };

    const roleTitles: Record<UserRole, { en: string; hi: string }> = {
        farmer: { en: 'Farmer', hi: 'किसान' },
        buyer: { en: 'Buyer', hi: 'खरीदार' },
        transporter: { en: 'Transporter', hi: 'ट्रांसपोर्टर' },
        government: { en: 'Government', hi: 'सरकारी' },
        admin: { en: 'Admin', hi: 'एडमिन' },
        supplier: { en: 'Supplier', hi: 'आपूर्तिकर्ता' }
    };

    return (
        <div className="min-h-screen relative overflow-hidden" style={{ background: 'linear-gradient(180deg, #e8f5f0 0%, #f0fdf4 50%, #ffffff 100%)' }}>
            {/* Soft Background Blobs */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute -top-40 -right-40 w-80 h-80 bg-emerald-200/40 rounded-full blur-3xl" />
                <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-teal-200/40 rounded-full blur-3xl" />
            </div>

            {/* Header with Logo */}
            <div className="relative z-10 pt-12 pb-8 px-6 text-center">
                <div className="mb-6">
                    <Logo size="lg" className="mx-auto" animated />
                </div>
                <h1 className="text-3xl font-bold text-gray-800 mb-2 drop-shadow-md">
                    Welcome to ANNADATA
                </h1>
                <p className="text-xl text-emerald-600 font-hindi font-semibold drop-shadow-md">
                    अन्नदाता में आपका स्वागत है
                </p>
                <p className="text-base text-gray-500 mt-2">
                    India's #1 Smart Farming Platform
                </p>
            </div>

            <div className="relative z-10 px-6 pb-12">
                {/* Login Mode Selection */}
                {mode === 'select' && (
                    <div className="space-y-6 animate-in fade-in duration-300">
                        {/* Regular Login Button */}
                        <button
                            onClick={() => setMode('mobile')}
                            aria-label="Login with mobile number"
                            className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white p-5 rounded-2xl shadow-lg shadow-emerald-200 flex items-center justify-between group hover:scale-[1.02] transition-transform"
                        >
                            <div className="flex items-center gap-4">
                                <div className="p-3 bg-white/20 rounded-xl backdrop-blur">
                                    <Phone size={24} />
                                </div>
                                <div className="text-left">
                                    <p className="font-bold text-lg">Login with Mobile</p>
                                    <p className="text-emerald-100 text-sm font-hindi">मोबाइल से लॉगिन करें</p>
                                </div>
                            </div>
                            <ChevronRight size={24} className="group-hover:translate-x-1 transition-transform" />
                        </button>

                        {/* Developer Mode Section */}
                        <div className="bg-white/80 backdrop-blur-lg rounded-3xl p-6 border border-gray-100 shadow-xl shadow-gray-100/50">
                            <div className="flex items-center gap-3 mb-5">
                                <div className="p-2 bg-indigo-100 rounded-lg">
                                    <Zap size={20} className="text-indigo-600" />
                                </div>
                                <div>
                                    <h2 className="font-bold text-lg text-gray-800">Developer Mode</h2>
                                    <p className="text-sm text-gray-500">Quick login for demo & testing</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                {demoUsers.map((user) => (
                                    <button
                                        key={user.id}
                                        onClick={() => handleDevLogin(user.role)}
                                        disabled={isLoading}
                                        className={`p-4 rounded-xl text-white bg-gradient-to-br ${roleColors[user.role]} text-left hover:scale-[1.03] transition-transform disabled:opacity-50 shadow-md`}
                                    >
                                        <div className="flex items-center gap-2 mb-2">
                                            {roleIcons[user.role]}
                                            <span className="text-xs font-bold uppercase opacity-90">
                                                {roleTitles[user.role].en}
                                            </span>
                                        </div>
                                        <p className="text-sm font-medium truncate">{user.name}</p>
                                        <p className="text-xs opacity-80 font-hindi">{roleTitles[user.role].hi}</p>
                                    </button>
                                ))}
                            </div>

                            <p className="text-sm text-center text-gray-400 mt-4">
                                ⚡ Instant login without OTP verification
                            </p>
                        </div>
                    </div>
                )}

                {/* Mobile Number Input */}
                {mode === 'mobile' && (
                    <div className="space-y-6 animate-in fade-in duration-300">
                        <button
                            onClick={() => setMode('select')}
                            className="text-base text-gray-600 hover:text-gray-800 flex items-center gap-1"
                        >
                            ← Back
                        </button>

                        <div className="bg-white rounded-3xl p-6 shadow-xl shadow-gray-100/50 border border-gray-100">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="p-3 bg-emerald-100 rounded-xl">
                                    <Phone size={24} className="text-emerald-600" />
                                </div>
                                <div>
                                    <h2 className="font-bold text-xl text-gray-800">Enter Mobile Number</h2>
                                    <p className="text-sm text-gray-500 font-hindi">मोबाइल नंबर दर्ज करें</p>
                                </div>
                            </div>

                            <div className="flex items-center bg-gray-50 rounded-2xl overflow-hidden border-2 border-gray-200 mb-4">
                                <span className="px-4 py-4 bg-gray-100 text-gray-600 font-bold text-lg">+91</span>
                                <input
                                    type="tel"
                                    value={mobile}
                                    onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                                    placeholder="Enter 10-digit number…"
                                    autoComplete="tel"
                                    inputMode="numeric"
                                    aria-label="Mobile phone number"
                                    className="flex-1 px-4 py-4 bg-transparent outline-none text-xl font-medium text-gray-800 placeholder:text-gray-400"
                                />
                            </div>

                            {error && (
                                <p className="text-red-500 text-sm mb-4 font-medium">{error}</p>
                            )}

                            <button
                                onClick={handleSendOTP}
                                disabled={mobile.length !== 10 || isLoading}
                                className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white py-4 rounded-2xl font-bold text-lg disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg transition-shadow"
                            >
                                {isLoading ? 'Sending...' : 'Send OTP'}
                            </button>

                            <p className="text-xs text-center text-gray-400 mt-4">
                                Admin Login: 6360248176
                            </p>
                        </div>
                    </div>
                )}

                {/* OTP Verification */}
                {mode === 'otp' && (
                    <div className="space-y-6 animate-in fade-in duration-300">
                        <button
                            onClick={() => setMode('mobile')}
                            className="text-base text-gray-600 hover:text-gray-800 flex items-center gap-1"
                        >
                            ← Change Number
                        </button>

                        <div className="bg-white rounded-3xl p-6 shadow-xl shadow-gray-100/50 border border-gray-100">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="p-3 bg-blue-100 rounded-xl">
                                    <Shield size={24} className="text-blue-600" />
                                </div>
                                <div>
                                    <h2 className="font-bold text-xl text-gray-800">Verify OTP</h2>
                                    <p className="text-sm text-gray-500">Sent to +91 {mobile}</p>
                                </div>
                            </div>

                            <div className="flex justify-center gap-4 mb-6">
                                {[0, 1, 2, 3].map((i) => (
                                    <input
                                        key={i}
                                        type="text"
                                        maxLength={1}
                                        value={otp[i] || ''}
                                        onChange={(e) => {
                                            const newOtp = otp.split('');
                                            newOtp[i] = e.target.value;
                                            setOtp(newOtp.join(''));
                                            if (e.target.value && e.target.nextElementSibling) {
                                                (e.target.nextElementSibling as HTMLInputElement).focus();
                                            }
                                        }}
                                        autoComplete="one-time-code"
                                        inputMode="numeric"
                                        aria-label={`OTP digit ${i + 1}`}
                                        className="w-14 h-14 text-center text-2xl font-bold bg-gray-50 border-2 border-gray-200 rounded-xl outline-none focus:border-emerald-500 transition-colors text-gray-800"
                                    />
                                ))}
                            </div>

                            {error && (
                                <p className="text-red-500 text-sm text-center mb-4 font-medium">{error}</p>
                            )}

                            <button
                                onClick={handleVerifyOTP}
                                disabled={otp.length !== 4 || isLoading}
                                className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white py-4 rounded-2xl font-bold text-lg disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg transition-shadow"
                            >
                                {isLoading ? 'Verifying...' : 'Verify & Login'}
                            </button>

                            <p className="text-xs text-center text-gray-400 mt-4">
                                Demo: Enter any 4 digits
                            </p>
                        </div>
                    </div>
                )}

                {/* Register Link */}
                <div className="mt-8 text-center">
                    <p className="text-gray-600 text-sm">
                        New user? / नए उपयोगकर्ता?{' '}
                        <Link to="/register" className="text-emerald-600 font-bold hover:underline">
                            Create Account
                        </Link>
                    </p>
                </div>

                {/* Footer */}
                <div className="mt-8 text-center">
                    <p className="text-gray-400 text-xs">
                        © 2026 Lattice Consulting Worldwide (OPC) Pvt. Ltd.
                    </p>
                </div>
            </div>
        </div>
    );
}

export default LoginPage;
